import React, { useState } from "react";
import {
  Avatar,
  AvatarGroup,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  Tooltip,
} from "@fluentui/react-components";
import PropTypes from "prop-types";
import FluentUiIcon from "../../../assets/fluent-ui-icon";
import "../dashboard-util/dashboard-telerik-helper/deal-team-avatar.scss";

const memberShape = PropTypes.shape({
  IsOwner: PropTypes.bool,
  IsStaffer: PropTypes.bool,
  IsSeniorDealMember: PropTypes.bool,
  User: PropTypes.shape({
    Name: PropTypes.string,
  }),
});

const getColor = (isOwner, isSenior, isStaffer) => {
  if (isStaffer) return "brand";
  if (isOwner) return "blue";
  if (isSenior) return "seafoam";
  return "blue";
};

// Sort order: 1) Legitimate Staffer-owner
//             2) Owner who is not a Staffer (Banker assigned as owner)
//             3) Staffer, not an owner
//             4) Regular members
//             5) Senior deal members
const getMemberCategory = (member) => {
  if (member.IsOwner && member.IsStaffer) return 0;
  if (member.IsOwner) return 1;
  if (member.IsStaffer) return 2;
  if (member.IsSeniorDealMember) return 4;
  return 3;
};

const sortMembers = (members) => {
  return [...members].sort((a, b) => getMemberCategory(a) - getMemberCategory(b));
};

const TechAvatarWithBadge = ({ member, size = 24 }) => {
  const isStafferNonOwner = member.IsStaffer && !member.IsOwner;
  const isOwnerNonStaffer = member.IsOwner && !member.IsStaffer;

  return (
    <Avatar
      name={member.User?.Name || "N/A"}
      color={getColor(member.IsOwner, member?.IsSeniorDealMember, member?.IsStaffer)}
      size={size}
      badge={(isStafferNonOwner || isOwnerNonStaffer) ? { status: "blocked" } : undefined}
      title={member.User?.Name || "N/A"}
    />
  );
};

TechAvatarWithBadge.propTypes = {
  member: memberShape.isRequired,
  size: PropTypes.number,
};

const TechDealTeamAvatarGroupCell = ({ members }) => {
  const [open, setOpen] = useState(false);

  if (!members || !Array.isArray(members) || members.length === 0) {
    return <span>–</span>;
  }

  const sortedMembers = sortMembers(members);
  const shownMembers = sortedMembers.slice(0, 3);
  const extraMembers = sortedMembers.length > 3 ? sortedMembers.length - 3 : 0;

  const handleMouseEnter = () => setOpen(true);
  const handleMouseLeave = () => setOpen(false);

  return (
    <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <Popover
        open={open}
        onOpenChange={(e, data) => setOpen(data.open)}
        positioning={{ position: "below-start" }}
      >
        <PopoverTrigger disableButtonEnhancement>
          <div className="deal-team-avatar__trigger">
            <AvatarGroup layout="stack">
              {shownMembers.map((member, index) => (
                <div
                  key={index}
                  className="deal-team-avatar__stacked-item"
                  style={{ zIndex: 10 - index }}
                >
                  <TechAvatarWithBadge member={member} size={24} />
                </div>
              ))}
              {extraMembers > 0 && (
                <div className="deal-team-avatar__stacked-item--extra">
                  <Avatar
                    initials={`+${extraMembers}`}
                    color="neutral"
                    size={24}
                  />
                </div>
              )}
            </AvatarGroup>
          </div>
        </PopoverTrigger>

        <PopoverSurface>
          <div className="deal-team-avatar__popover-list">
            {sortedMembers.map((member, index) => {
              const isStafferMember = member?.IsStaffer && !member?.IsOwner;
              const isOwnerNonStaffer = member?.IsOwner && !member?.IsStaffer;
              return (
                <div key={index} className="deal-team-avatar__popover-row">
                  <TechAvatarWithBadge member={member} size={24} />
                  <span className="deal-team-avatar__member-name">
                    {member?.User?.Name || "N/A"}
                  </span>
                  {isStafferMember && (
                    <Tooltip
                      content="This Staffer is a Member on this Deal, not an Owner. A Deal Owner can reassign them as an Owner to give them full Staffing permissions."
                      relationship="description"
                      withArrow
                    >
                      <span
                        tabIndex={-1}
                        aria-label="Member info"
                        className="deal-team-avatar__member-info-icon"
                      >
                        <FluentUiIcon name="InfoRegular" size={14} color="#8a8a8a" />
                      </span>
                    </Tooltip>
                  )}
                  {isOwnerNonStaffer && (
                    <Tooltip
                      content={<>This User has a <strong>Banker</strong> Role, but is assigned as an Owner on this Deal. Consider making them a Member so that they can start logging their Weekly Hours and Estimates for this Deal.</>}
                      relationship="description"
                      withArrow
                    >
                      <span
                        tabIndex={-1}
                        aria-label="Owner info"
                        className="deal-team-avatar__member-info-icon"
                      >
                        <FluentUiIcon name="InfoRegular" size={14} color="#8a8a8a" />
                      </span>
                    </Tooltip>
                  )}
                </div>
              );
            })}
          </div>
        </PopoverSurface>
      </Popover>
    </div>
  );
};

TechDealTeamAvatarGroupCell.propTypes = {
  members: PropTypes.arrayOf(memberShape),
};

export default TechDealTeamAvatarGroupCell;
